import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/api';
import { consolidateOrdersByVendorId, ConsolidatedOrder } from '../../utils/consolidate-orders';
import { useOrders } from '../../hooks/queries';
import {
  buildOrderLocationState,
  buildTimelineLocationState,
  orderListReturn,
} from '../../utils/order-navigation';
import {
  buildOrdersListPath,
  getOrderStatusTabLabel,
  isMoreStatusFilter,
  ORDER_STATUS_MORE_TABS,
  ORDER_STATUS_PRIMARY_TABS,
  parseOrderStatusFilterFromSearch,
  type OrderStatusFilter,
} from '../../utils/order-status-filters';

export function OrderList() {
  const navigate = useNavigate();
  const location = useLocation();
  const statusFilter = parseOrderStatusFilterFromSearch(location.search);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [searchInput, setSearchInput] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchType, setSearchType] = useState<'order-id' | 'email' | null>(null);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [sortState, setSortState] = useState<{
    column: 'id' | 'date' | 'total' | 'status' | null;
    direction: 'asc' | 'desc';
  }>({ column: null, direction: 'desc' });
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);

  const { data: ordersResponse, isLoading: ordersLoading, error: queryError } = useOrders(
    page,
    limit,
    statusFilter,
  );
  const fetchedOrders = Array.isArray(ordersResponse?.data) ? ordersResponse.data : [];
  const isSearchActive = Boolean(activeSearch.trim());
  const orders = (isSearching || isSearchActive) ? searchResults : fetchedOrders;
  const loading = ordersLoading && !isSearching;
  const error = !isSearching && queryError ? (queryError as any).message ?? 'Failed to fetch orders' : null;

  const runSearch = async (term: string): Promise<void> => {
    const trimmedSearch = term.trim();
    setActiveSearch(trimmedSearch);

    if (!trimmedSearch) {
      setSearchResults([]);
      setIsSearching(false);
      setSearchError(null);
      setSearchType(null);
      return;
    }

    const searchId = parseInt(trimmedSearch, 10);
    const isEmail = trimmedSearch.includes('@');
    const isNumeric = !Number.isNaN(searchId);

    if (isNumeric && !isEmail) {
      await searchOrderById(searchId);
      return;
    }

    await searchOrdersByEmail(trimmedSearch);
  };

  const handleSearchSubmit = (event?: React.FormEvent): void => {
    event?.preventDefault();
    void runSearch(searchInput);
  };

  const handleClearSearch = (): void => {
    setSearchInput('');
    void runSearch('');
  };

  useEffect(() => {
    setPage(1);
  }, [location.search]);

  const handleStatusFilterChange = (filter: OrderStatusFilter) => {
    setPage(1);
    navigate(buildOrdersListPath(filter), { replace: true });
    setMoreMenuOpen(false);
  };

  const searchOrderById = async (orderId: number): Promise<void> => {
    setIsSearching(true);
    setSearchType('order-id');
    setSearchError(null);
    setSearchResults([]);
    try {
      const response = await apiClient.getOrderById(orderId);
      if (response.success && response.data) {
        // Wrap single order in array for consistent display
        setSearchResults([response.data]);
      } else {
        setSearchError(response.message || 'Order not found');
        setSearchResults([]);
      }
    } catch (err: any) {
      // Check if it's a 404 (order not found)
      if (err.status === 404 || err.message?.includes('404') || err.message?.includes('not found')) {
        setSearchError(`Order ID ${orderId} not found`);
        setSearchResults([]);
      } else {
        setSearchError(err.message || 'An error occurred while searching');
        setSearchResults([]);
      }
    } finally {
      setIsSearching(false);
    }
  };

  const searchOrdersByEmail = async (searchValue: string) => {
    setIsSearching(true);
    setSearchError(null);
    setSearchResults([]);
    setSearchType('email');

    try {
      // Emails are obfuscated in API responses, so matching must happen server-side.
      const response = await apiClient.searchOrdersByEmail(searchValue);
      if (!response.success) {
        setSearchError(response.message || 'An error occurred while searching');
        setSearchResults([]);
        return;
      }

      const matchedOrders = Array.isArray(response.data) ? response.data : [];
      if (matchedOrders.length > 0) {
        setSearchResults(matchedOrders);
      } else {
        setSearchError(`No orders found matching email: ${searchValue}`);
        setSearchResults([]);
      }
    } catch (err: any) {
      setSearchError(err.message || 'An error occurred while searching');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // CRITICAL: getComparator MUST be defined before sortedOrders useMemo
  // due to JavaScript temporal dead zone - const functions cannot be accessed before declaration
  const getComparator = (
    column: 'id' | 'date' | 'total' | 'status',
    direction: 'asc' | 'desc'
  ) => {
    switch (column) {
      case 'id':
        return (a: any, b: any) => {
          const aVal = a.id ?? 0;
          const bVal = b.id ?? 0;
          return direction === 'asc' ? aVal - bVal : bVal - aVal;
        };

      case 'date':
        return (a: any, b: any) => {
          const maxDate = new Date(8640000000000000);
          const aDate = a.date_created ? new Date(a.date_created) : maxDate;
          const bDate = b.date_created ? new Date(b.date_created) : maxDate;
          return direction === 'asc' ? aDate.getTime() - bDate.getTime() : bDate.getTime() - aDate.getTime();
        };

      case 'total':
        return (a: any, b: any) => {
          const aVal = parseFloat(a.total_inc_tax ?? a.total ?? '0') || 0;
          const bVal = parseFloat(b.total_inc_tax ?? b.total ?? '0') || 0;
          return direction === 'asc' ? aVal - bVal : bVal - aVal;
        };

      case 'status':
        return (a: any, b: any) => {
          const aVal = (a.status || '').toLowerCase();
          const bVal = (b.status || '').toLowerCase();
          const comparison = aVal.localeCompare(bVal);
          return direction === 'asc' ? comparison : -comparison;
        };

      default:
        return () => 0;
    }
  };

  const handleSort = (column: 'id' | 'date' | 'total' | 'status') => {
    if (loading) {
      return; // Prevent sorting during loading (per FR-010)
    }

    setSortState(prev => {
      if (prev.column === column) {
        // Toggle direction
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      } else {
        // New column, start with ascending
        return { column, direction: 'asc' };
      }
    });
  };

  const getSortIndicator = (column: 'id' | 'date' | 'total' | 'status') => {
    if (sortState.column !== column) {
      return null;
    }
    return sortState.direction === 'asc' ? '↑' : '↓';
  };

  // Consolidate orders by Vendor ID (customer_id) before sorting
  const consolidatedOrders: ConsolidatedOrder[] = useMemo(() => {
    return consolidateOrdersByVendorId(orders);
  }, [orders]);

  // Sort orders based on sortState
  // NOTE: getComparator must be defined above this useMemo due to temporal dead zone
  const sortedOrders = useMemo(() => {
    if (!sortState.column) {
      // Default: sort by ID descending (current behavior)
      return [...consolidatedOrders].sort((a, b) => (b.id || 0) - (a.id || 0));
    }

    const comparator = getComparator(sortState.column, sortState.direction);
    return [...consolidatedOrders].sort(comparator);
  }, [consolidatedOrders, sortState]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getStatusClass = (status: string): string => {
    const s = (status || '').toLowerCase();
    if (['completed', 'shipped', 'fulfilled'].some(k => s.includes(k))) { return 'badge-success'; }
    if (['pending', 'awaiting', 'processing'].some(k => s.includes(k))) { return 'badge-warning'; }
    if (['cancelled', 'canceled', 'declined', 'refunded', 'void'].some(k => s.includes(k))) { return 'badge-error'; }
    return 'badge-neutral';
  };

  const getPaymentMethodDisplay = (order: any) => {
    if (order.payment_method) {
      return order.payment_method;
    }
    if (order.payment_provider_id) {
      const providers: Record<string, string> = {
        'authorizenet': 'Authorize.net',
        'stripe': 'Stripe',
        'paypal': 'PayPal',
        'square': 'Square',
        'manual': 'Manual',
      };
      return providers[order.payment_provider_id] || order.payment_provider_id;
    }
    return 'N/A';
  };

  const handleMenuToggle = (orderId: number, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click
    setOpenMenuId(prev => prev === orderId ? null : orderId);
  };

  const handleViewTimeline = (orderId: number) => {
    navigate(
      `/orders/${orderId}/timeline`,
      { state: buildTimelineLocationState(orderId, orderListReturn(statusFilter)) },
    );
    setOpenMenuId(null);
  };

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (_event: MouseEvent) => {
      if (openMenuId !== null) {
        setOpenMenuId(null);
      }
      if (moreMenuOpen) {
        setMoreMenuOpen(false);
      }
    };

    if (openMenuId !== null || moreMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openMenuId, moreMenuOpen]);

  if (loading && !isSearching) {
    return (
      <div className="page-container">
        <div className="loading-state"><div className="spinner" /> Loading orders…</div>
      </div>
    );
  }

  if (error && !activeSearch) {
    return (
      <div className="page-container">
        <div className="error-banner">⚠ {error}</div>
      </div>
    );
  }

  const statusFilterLabel = getOrderStatusTabLabel(statusFilter);
  const searchTypeLabel = searchType === 'order-id' ? 'Order ID' : searchType === 'email' ? 'Email' : 'search term';
  const moreFilterActive = isMoreStatusFilter(statusFilter);

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Orders</h1>
          <p className="page-subtitle">
            {isSearching
              ? `Searching by ${searchTypeLabel}…`
              : activeSearch
              ? `${sortedOrders.length} result${sortedOrders.length !== 1 ? 's' : ''} for "${activeSearch}"`
              : statusFilter === 'all'
              ? `${sortedOrders.length} order${sortedOrders.length !== 1 ? 's' : ''} · Page ${page}`
              : `${sortedOrders.length} ${statusFilterLabel.toLowerCase()} · Page ${page}`}
          </p>
        </div>
        <div className="page-actions">
          <form
            className="search-wrap"
            onSubmit={handleSearchSubmit}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <span className="search-icon">🔍</span>
            <input
              className="search-input"
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Order ID or email…"
            />
            <button type="submit" className="btn btn-primary btn-sm">
              Search
            </button>
          </form>
          {activeSearch ? (
            <button className="btn btn-secondary btn-sm" onClick={handleClearSearch} type="button">
              Clear
            </button>
          ) : null}
          <div className="pagination">
            <button className="btn btn-secondary btn-sm" onClick={() => setPage(page - 1)} disabled={page === 1 || !!activeSearch}>← Prev</button>
            <button className="btn btn-secondary btn-sm" onClick={() => setPage(page + 1)} disabled={fetchedOrders.length < limit || !!activeSearch}>Next →</button>
          </div>
        </div>
      </div>

      {!activeSearch && (
        <div className="tab-bar" style={{ marginBottom: '1rem', flexWrap: 'wrap', gap: '0.25rem' }}>
          {ORDER_STATUS_PRIMARY_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`tab-btn ${statusFilter === tab.id ? 'active' : ''}`}
              onClick={() => handleStatusFilterChange(tab.id)}
              disabled={loading}
            >
              {tab.label}
            </button>
          ))}
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <button
              type="button"
              className={`tab-btn ${moreFilterActive ? 'active' : ''}`}
              onClick={(event) => {
                event.stopPropagation();
                setMoreMenuOpen((open) => !open);
              }}
              disabled={loading}
              aria-haspopup="menu"
              aria-expanded={moreMenuOpen}
            >
              More{moreFilterActive ? `: ${statusFilterLabel}` : ''} ▾
            </button>
            {moreMenuOpen && (
              <div
                role="menu"
                onClick={(event) => event.stopPropagation()}
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  zIndex: 20,
                  minWidth: '220px',
                  marginTop: '0.25rem',
                  background: 'var(--surface, #fff)',
                  border: '1px solid var(--border, #ddd)',
                  borderRadius: '6px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  padding: '0.35rem 0',
                }}
              >
                {ORDER_STATUS_MORE_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    role="menuitem"
                    className={`tab-btn ${statusFilter === tab.id ? 'active' : ''}`}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      borderRadius: 0,
                      border: 'none',
                    }}
                    onClick={() => handleStatusFilterChange(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {searchError && (
        <div className="error-banner" style={{ background: 'var(--color-warning-bg)', borderColor: 'var(--color-warning-border)', color: 'var(--color-warning)' }}>
          {searchError}
        </div>
      )}

      {sortedOrders.length === 0 && !isSearching ? (
        <div className="table-wrap">
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <p className="empty-state-title">
              {activeSearch
                ? 'No orders found'
                : statusFilter === 'all'
                ? 'No orders yet'
                : `No ${statusFilterLabel.toLowerCase()} orders`}
            </p>
            {activeSearch && <p className="empty-state-desc">Try a different search term.</p>}
          </div>
        </div>
      ) : (
        <>
        {/* ── Mobile card list (hidden on desktop via CSS) ── */}
        <div className="order-cards">
          {sortedOrders.map((order) => (
            <div
              key={order._isConsolidated ? `consolidated-${order.customer_id}` : order.id}
              className="order-card"
              onClick={() => navigate(`/orders/${order.id}`, { state: buildOrderLocationState(orderListReturn(statusFilter)) })}
            >
              <div className="order-card-row1">
                <span className="order-card-id">#{order.id}</span>
                <span className="order-card-total">{formatCurrency(parseFloat(order.total_inc_tax || order.total || '0'))}</span>
              </div>
              <div className="order-card-row2">
                <span className={`badge ${getStatusClass(order.status)}`}>{order.status || 'Unknown'}</span>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{order.items_total || 0} item{(order.items_total || 0) !== 1 ? 's' : ''}</span>
              </div>
              <div className="order-card-row3">
                <span>{order.billing_address?.email || order.customer?.email || '—'}</span>
                <span>{formatDate(order.date_created).split(',')[0]}</span>
              </div>
            </div>
          ))}
        </div>

        {/* ── Desktop table (hidden on mobile via CSS) ── */}
        <div className="table-wrap">
          <table className="data-table" style={{ minWidth: 900 }}>
            <thead>
              <tr>
                <th className="sortable" onClick={() => handleSort('id')}>Order ID <span className="sort-indicator">{getSortIndicator('id')}</span></th>
                <th className="sortable" onClick={() => handleSort('date')}>Date <span className="sort-indicator">{getSortIndicator('date')}</span></th>
                <th>Vendor ID</th>
                <th className="sortable" onClick={() => handleSort('status')}>Status <span className="sort-indicator">{getSortIndicator('status')}</span></th>
                <th>Payment</th>
                <th>Customer Email</th>
                <th>Items</th>
                <th className="sortable col-amount" onClick={() => handleSort('total')}>Total <span className="sort-indicator">{getSortIndicator('total')}</span></th>
                <th className="col-actions"></th>
              </tr>
            </thead>
            <tbody>
              {sortedOrders.map((order) => (
                <tr
                  key={order._isConsolidated ? `consolidated-${order.customer_id}` : order.id}
                  className="clickable"
                  onClick={() => navigate(`/orders/${order.id}`, { state: buildOrderLocationState(orderListReturn(statusFilter)) })}
                >
                  <td style={{ fontWeight: 600 }}>
                    #{order.id}
                    {order._isConsolidated && (
                      <span
                        className="consolidation-badge"
                        title={`Consolidated from orders: ${order._consolidatedOrderIds.join(', ')}`}
                      >
                        {order._consolidatedOrderIds.length} orders
                      </span>
                    )}
                  </td>
                  <td className="col-date" style={{ color: 'var(--text-secondary)' }}>{formatDate(order.date_created)}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>{order.customer_id || '—'}</td>
                  <td><span className={`badge ${getStatusClass(order.status)}`}>{order.status || 'Unknown'}</span></td>
                  <td style={{ color: 'var(--text-secondary)' }}>{getPaymentMethodDisplay(order)}</td>
                  <td style={{ fontWeight: 500 }}>{order.billing_address?.email || order.customer?.email || '—'}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>{order.items_total || 0}</td>
                  <td className="col-amount amount">{formatCurrency(parseFloat(order.total_inc_tax || order.total || '0'))}</td>
                  <td className="col-actions" onClick={(e) => e.stopPropagation()}>
                    <div className="action-menu-wrap" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'flex-end' }}>
                      <button
                        className="timeline-link-btn"
                        onClick={() => handleViewTimeline(order.id)}
                        aria-label={`View timeline for order ${order.id}`}
                      >
                        📋 Timeline
                      </button>
                      <button className="action-menu-btn" onClick={(e) => handleMenuToggle(order.id, e)} aria-label="More order actions">⋯</button>
                      {openMenuId === order.id && (
                        <div className="action-menu-dropdown" role="menu">
                          <button className="action-menu-item" role="menuitem" onClick={() => handleViewTimeline(order.id)}>
                            📋 View Timeline
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
}
